<template>
  <Dialog
    :visible="visible"
    modal
    header="Add your pre-JamX downloads"
    :closable="false"
    :dismissableMask="false"
    :closeOnEscape="false"
    :draggable="false"
    :style="{ width: '640px', maxWidth: '95vw' }"
  >
    <div class="tw-flex tw-flex-col tw-gap-3">
      <div class="tw-text-sm tw-text-slate-600 dark:tw-text-slate-200">
        Help us classify your shows by sharing the average monthly downloads
        from before you joined JamX.
      </div>

      <div
        v-if="loading"
        class="tw-flex tw-gap-2 tw-items-center tw-text-sm tw-text-slate-500"
      >
        <i class="pi pi-spin pi-spinner" />
        <span>Loading show details...</span>
      </div>

      <div v-else>
        <Accordion :multiple="true">
          <AccordionTab
            v-for="show in shows"
            :key="show.show_id"
            :header="accordionHeader(show)"
          >
            <div class="tw-flex tw-flex-col tw-gap-2">
              <div class="tw-text-xs tw-text-slate-500">
                Add the average monthly downloads this show received before
                joining JamX.
              </div>
              <div class="tw-flex tw-gap-2 tw-items-center">
                <InputNumber
                  v-model="formValues[show.show_id]"
                  :min="0"
                  :max="MAX_AVG_MONTHLY_DOWNLOADS"
                  :useGrouping="true"
                  class="w-full"
                  inputClass="w-full"
                  placeholder="e.g. 12000"
                />
                <a
                  :href="`/#/podcaster/add-show/${show.show_id}`"
                  target="_blank"
                  class="tw-text-xs tw-text-[var(--primary-color)] hover:tw-underline tw-text-nowrap"
                >
                  Edit show
                </a>
              </div>
              <small
                v-if="!isShowComplete(show)"
                class="tw-text-red-500 tw-text-xs"
              >
                Please provide a value to complete this show.
              </small>
            </div>
          </AccordionTab>
        </Accordion>
      </div>
      <div
        v-if="errorMessage"
        class="tw-text-xs tw-text-red-500 tw-bg-red-50 dark:tw-bg-red-900/30 tw-rounded tw-p-2"
      >
        {{ errorMessage }}
      </div>
      <div
        class="tw-flex tw-justify-between tw-items-center tw-gap-2 tw-pt-2 tw-flex-wrap"
      >
        <Button
          label="Remind me later"
          class="btn-brand"
          outlined
          @click="handleRemindLater"
        />
        <Button
          :label="allComplete ? 'Save downloads' : 'Cancel'"
          class="btn-brand"
          :loading="allComplete ? saving : false"
          @click="allComplete ? submit() : handleCancel()"
        />
      </div>
      <div
        class="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300"
      >
        <Checkbox v-model="dontShowAgain" binary inputId="prejamx-hide" />
        <label for="prejamx-hide" class="tw-m-0 tw-cursor-pointer">
          Don't show again
        </label>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { defineEmits, defineProps, ref, watch, computed } from "vue";
import Dialog from "primevue/dialog";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import { MAX_AVG_MONTHLY_DOWNLOADS } from "@/consts";

const props = defineProps({
  visible: Boolean,
  shows: { type: Array, default: () => [] },
  saving: Boolean,
  loading: Boolean,
});

const emit = defineEmits(["save", "remind-later", "dismiss"]);

const formValues = ref({});
const errorMessage = ref("");
const dontShowAgain = ref(false);

const isMissingValue = (v) => v == null || Number(v) <= 0;

watch(
  () => props.shows,
  (shows) => {
    formValues.value = (shows || []).reduce((acc, show) => {
      const existing = formValues.value[show.show_id];
      acc[show.show_id] = existing ?? null;
      return acc;
    }, {});
  },
  { immediate: true }
);

const isShowComplete = (show) => {
  const inputVal = formValues.value[show.show_id];
  return !isMissingValue(inputVal);
};

const accordionHeader = (show) => `${show.show_title || "Show"}`;

const missingShowIds = computed(() =>
  (props.shows || [])
    .filter((s) => isMissingValue(formValues.value[s.show_id]))
    .map((s) => s.show_id)
);

const allComplete = computed(() => missingShowIds.value.length === 0);

const submit = () => {
  if (!allComplete.value) {
    errorMessage.value =
      "Please fill in the average monthly downloads for each show or click Remind me later.";
    return;
  }

  errorMessage.value = "";

  const updates = (props.shows || [])
    .map((show) => {
      const v = formValues.value[show.show_id];
      if (!isMissingValue(v)) {
        return { show_id: show.show_id, avg_per_month: Number(v) };
      }
      return null;
    })
    .filter(Boolean);

  emit("save", updates);
};

const handleRemindLater = () => {
  emit("remind-later");
};

const handleCancel = () => {
  emit("dismiss", { dontShowAgain: dontShowAgain.value });
};
</script>
